import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, RefreshCw, AlertTriangle, ExternalLink, BellRing, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const LEVEL = {
  ok: { bar: "bg-emerald-500", text: "text-emerald-700", ring: "border-emerald-200 bg-emerald-50" },
  warning: { bar: "bg-amber-500", text: "text-amber-700", ring: "border-amber-200 bg-amber-50" },
  critical: { bar: "bg-red-500", text: "text-red-700", ring: "border-red-200 bg-red-50" },
};

export default function AdminApifyUsage() {
  const [u, setU] = useState(null);
  const [busy, setBusy] = useState(false);
  // config
  const [cfg, setCfg] = useState(null);
  const [threshold, setThreshold] = useState(80);
  const [email, setEmail] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = () => { setBusy(true); api.get("/admin/apify-usage").then((r) => setU(r.data)).catch(() => setU({ available: false, reason: "Erreur de chargement." })).finally(() => setBusy(false)); };
  const loadCfg = () => api.get("/admin/apify-alert-config").then((r) => {
    setCfg(r.data); setThreshold(r.data.threshold); setEmail(r.data.email); setEnabled(r.data.enabled);
  }).catch(() => {});
  useEffect(() => { load(); loadCfg(); /* eslint-disable-next-line */ }, []);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const r = await api.put("/admin/apify-alert-config", { threshold: Number(threshold), email: email.trim(), enabled });
      setCfg(r.data); setThreshold(r.data.threshold); setEmail(r.data.email); setEnabled(r.data.enabled);
      toast.success("Réglages d'alerte Apify enregistrés.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enregistrement impossible.");
    } finally { setSavingCfg(false); }
  };

  const lv = LEVEL[u?.level] || LEVEL.ok;
  const dirty = cfg && (Number(threshold) !== cfg.threshold || email.trim() !== cfg.email || enabled !== cfg.enabled);

  return (
    <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-apify-usage">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-xl text-[#0F172A]"><Activity className="h-5 w-5 text-[#C6A87C]" /> Usage Apify du mois</h2>
        <button onClick={load} disabled={busy} data-testid="admin-apify-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>

      {!u ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : !u.available ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500" data-testid="admin-apify-unavailable">
          {u.reason || "Données Apify indisponibles."} {u.plan ? `(compte ${u.username || ""}, plan ${u.plan})` : ""}
        </div>
      ) : (
        <div className={`rounded-xl border p-4 ${lv.ring}`} data-testid="admin-apify-card">
          <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
            <div>
              <p className="text-sm text-slate-500">Compte <b className="text-[#0F172A]">{u.username}</b> · plan <b className="text-[#0F172A]">{u.plan}</b></p>
              <p className={`text-2xl font-bold tabular-nums ${lv.text}`} data-testid="admin-apify-value">{u.used_usd.toFixed(2)} $ <span className="text-base font-medium text-slate-400">/ {u.max_usd.toFixed(2)} $</span></p>
            </div>
            <span className={`rounded-full px-3 py-1 text-sm font-bold ${lv.text}`} data-testid="admin-apify-pct">{u.pct}%</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-white/70 ring-1 ring-inset ring-slate-200">
            <div className={`h-full rounded-full transition-all ${lv.bar}`} style={{ width: `${Math.min(u.pct, 100)}%` }} data-testid="admin-apify-bar" />
          </div>
          <p className="mt-2 text-xs text-slate-500">Restant ce mois : <b className={lv.text}>{u.remaining_usd.toFixed(2)} $</b> · seuil d'alerte : <b>{u.threshold}%</b></p>

          {u.level !== "ok" && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-white/60 p-3 text-xs" data-testid="admin-apify-alert">
              <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${lv.text}`} />
              <span className="text-slate-600">
                {u.level === "critical"
                  ? "Plafond quasi atteint : les recherches d'annonces peuvent échouer (erreur 402). "
                  : "Vous approchez du plafond mensuel. "}
                Augmentez la limite dans la
                <a href="https://console.apify.com/settings/limits" target="_blank" rel="noreferrer" className="mx-1 inline-flex items-center gap-0.5 font-semibold text-[#C6A87C] underline">
                  Console Apify → Settings → Limits <ExternalLink className="h-3 w-3" />
                </a>
                ou rechargez votre compte.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Réglages de l'alerte e-mail automatique */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="admin-apify-alert-config">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"><BellRing className="h-4 w-4 text-[#C6A87C]" /> Alerte e-mail automatique</p>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="admin-apify-alert-enabled" className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-[#C6A87C]" />
            Activée
          </label>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Seuil d'alerte (% du plafond)</label>
            <div className="flex items-center gap-2">
              <input type="number" min={50} max={99} value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="admin-apify-alert-threshold" className="h-10 w-24 rounded-lg border border-slate-300 px-3 text-sm" />
              <span className="text-sm text-slate-400">%</span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Adresse e-mail de notification</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-apify-alert-email" placeholder="vous@exemple.com" className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" />
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-400">Un e-mail est envoyé dès que l'usage dépasse le seuil (puis au plus une fois toutes les 24 h tant qu'il reste dépassé).</p>
        <button onClick={saveCfg} disabled={savingCfg || !dirty} data-testid="admin-apify-alert-save"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e293b] disabled:opacity-40">
          {savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer les réglages
        </button>
      </div>
    </section>
  );
}
